/**
 * The last good answer to `GET /tenant`, per subdomain, so a rave boots with
 * no network.
 *
 * Everything else offline hangs off the tenant: which edition the address
 * names (`/2026`, resolved from the stored `editions`), and so which bundle
 * to read from IndexedDB. Without this, a phone in a field with the whole
 * lineup stored showed "Can't reach MyRave" before it ever looked.
 *
 * Platform-scoped storage (`myrave:platform:tenant-<subdomain>`): the tenant
 * is what CHOOSES the edition, so it cannot live under one. No colon in the
 * name — `storedEditionIds()` reads everything before the last colon as an id.
 */
import { ApiError, endpoints } from '@/lib/api';
import { readJson, remove, writeJson } from '@/lib/storage';
import { withTimeout } from './net';
import { signedInHere } from '@/lib/signOut';

export type TenantAnswer = Awaited<ReturnType<typeof endpoints.tenant>>;

const name = (subdomain: string) => `tenant-${subdomain}`;

/** Signed out is online only (the user, 2026-10-27): no stored answer is read, none is kept. */
export function storedTenant(subdomain: string): TenantAnswer | null {
  if (!signedInHere()) return null;
  return readJson<TenantAnswer | null>(null, name(subdomain), null);
}

/** A first visit with no stored answer may wait this long before it is an error. */
export const TENANT_FIRST_TIMEOUT_MS = 20_000;

export interface TenantLoadOptions {
  /** The fresh answer, when it lands after the stored one was served. */
  onLate?: (answer: TenantAnswer) => void;
  /** The server said 404 after a stored answer was served: this festival is gone. */
  onGone?: () => void;
}

/**
 * The stored answer AT ONCE when there is one, the network revalidating it
 * behind; with none, the network — with a ceiling, so a hang ends.
 *
 * A definitive 404 is never papered over: the stored copy is dropped and the
 * caller told, because the subdomain no longer has a festival. Anything else
 * (no signal, a hang, a 500) leaves the stored answer standing.
 *
 * The callbacks run on a later task, never inside this call: a result handed
 * to the query cache before the query's own result would be overwritten by it.
 */
/**
 * `/tenant`, taking the answer `index.html`'s inline script already asked for
 * (`offline/preconnectPlugin.ts`) — once. Anything but a successful answer
 * (no script, a failure, a 404) asks the normal way, so errors and offline
 * behave exactly as before.
 */
export async function askTenant(): Promise<TenantAnswer> {
  const holder = (globalThis as { __myraveEarly?: { tenant?: Promise<unknown> } }).__myraveEarly;
  const early = holder?.tenant;
  if (holder) delete holder.tenant;
  const answer = early ? await early.catch(() => null) : null;
  if (answer && typeof answer === 'object' && 'scope' in answer) return answer as TenantAnswer;
  return endpoints.tenant();
}

export async function loadTenant(subdomain: string | null, opts: TenantLoadOptions = {}): Promise<TenantAnswer> {
  if (!subdomain) return askTenant();

  const stored = storedTenant(subdomain);
  const network = askTenant().then((answer) => {
    if (answer.tenant && signedInHere()) writeJson(null, name(subdomain), answer);
    else remove(null, name(subdomain));
    return answer;
  });
  const gone = (err: unknown) => err instanceof ApiError && err.status === 404;

  if (stored) {
    network.then(
      (answer) => { setTimeout(() => opts.onLate?.(answer), 0); },
      (err) => {
        if (!gone(err)) return;
        remove(null, name(subdomain));
        setTimeout(() => opts.onGone?.(), 0);
      },
    );
    return stored;
  }

  try {
    return await withTimeout(network, TENANT_FIRST_TIMEOUT_MS);
  } catch (err) {
    network.catch(() => {});
    if (gone(err)) remove(null, name(subdomain));
    throw err;
  }
}
